import type { LayoutItem } from 'react-grid-layout';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  blockBelowDataTailHeightState,
  blockCanExpandState,
  blockColumnHeaderHeightState,
  blockContinuationPageCount,
  blockDataRowBoundariesState,
  blockDataRowHeightState,
  blockExpandedState,
  blockCustomExpandsByRowSlice,
  blockFullContentHeightState,
  blockOverflowErrorState,
  blockPrefaceHeightState,
  blockSourcePageVisibleHeight,
  blockTitleBarHeightState,
  blockTrailerHeightState,
  trailerScrollPaddingPx,
  isReportState,
  selectedBlocksState,
  type OverflowError,
} from 'venn-state';
import { ZIndex, GetColor, Icon } from 'venn-ui-kit';
import { assertExhaustive, useHasFF } from 'venn-utils';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { ContractBadge, ExpandBadge } from './components/core/expand-badge/ExpandBadge';
import { BorderContainer } from './components/shared/border-container';
import { BlockContext } from './contexts/block-context';
import { useAlertOutline } from './utils/hooks/use-alert-outline';
import { measureTrailerAndTail } from './utils/hooks/use-trailer-tail-measurement';
import { useAppPrintMode } from '../print';
import { GRID_COLUMN_HEADER_DATA_ATTR } from '../data-grid/DataGrid';
import { BLOCK_CONTAINER_CLASS } from './components/core';

interface ReportLabBlockContainerProps {
  id: string;
  children: React.ReactNode;
  blockLayout: LayoutItem;
  'data-testid'?: string;
  pageFooterRef: React.RefObject<HTMLElement>;
  pageNumber: number;
}

const findElementByDataAttr = (root: HTMLElement, dataAttr: string): HTMLElement | null => {
  const stack: HTMLElement[] = [root];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;
    if (current.hasAttribute(dataAttr)) return current;

    for (const child of Array.from(current.children)) {
      if (child instanceof HTMLElement) {
        stack.push(child);
      }
    }
  }

  return null;
};

/**
 * Returns the first AG-Grid data row (`.ag-row`) inside the block, excluding the column header.
 * Scoped to `.ag-center-cols-container` so we never pick up `.ag-header-row` (which AG-Grid
 * renders inside `.ag-header` and which repeats on every continuation slice as a separate band).
 */
const findFirstDataRow = (root: HTMLElement): HTMLElement | null =>
  root.querySelector<HTMLElement>('.ag-center-cols-container .ag-row');

/**
 * Returns ascending cumulative data-row bottom offsets, measured from the top of the first
 * `.ag-row`, so continuation slices can cut at real row boundaries instead of a pixel multiple
 * of a single sampled row height (which splits rows that wrap to more than one line). Dividing by
 * `scale` un-does the report page's `transform: scale(zoom)` so the offsets stay in the same
 * local pixel space as `sliceHeightPx` / `translateY`.
 */
const measureDataRowBoundaries = (rows: HTMLElement[], scale: number): number[] => {
  if (rows.length === 0) return [];
  const safeScale = scale > 0 ? scale : 1;
  const firstTop = rows[0]!.getBoundingClientRect().top;
  const bottoms = rows.map((r) => (r.getBoundingClientRect().bottom - firstTop) / safeScale);
  return Array.from(new Set(bottoms.filter((b) => b > 0))).sort((a, b) => a - b);
};

/**
 * Returns the natural content height of the block.
 *
 * Reads `.ag-root-wrapper.offsetHeight` (set inline by AG-Grid's `domLayout: 'autoHeight'` from
 * header + row data) plus the title-bar chrome. The inline height beats any ancestor `max-height`
 * or `height: 100%` cascade, so this reading is stable when we apply max-height clips to
 * `BlockContainer` / `OverflowErrorWrapper`. That stability is what breaks the Recoil ↔
 * ResizeObserver feedback loop a `scrollHeight` / flex-container sum would re-introduce, because
 * those readings track the slot box and oscillate as max-height is applied / removed.
 *
 * Returns `0` when AG-Grid is not yet in the DOM (suspense fallback / pre-mount). That matches
 * the `blockFullContentHeightState` default and the short-circuit in `blockContinuationPageCount`,
 * so no continuation page is forced into existence during the brief window before AG-Grid mounts.
 * The `MutationObserver` fires `scheduleMeasurement` once AG-Grid is in the DOM and the real value
 * lands on the next animation frame.
 *
 * All currently-expandable block types are AG-Grid based (see `expandableBlockTypes.ts`), so the
 * `.ag-root-wrapper` lookup is sufficient.
 */
const measureNaturalContentHeight = (agWrapper: HTMLElement | null, titleBarEl: HTMLElement | null): number => {
  if (!agWrapper || agWrapper.offsetHeight <= 0) return 0;
  return (titleBarEl?.offsetHeight ?? 0) + agWrapper.offsetHeight;
};

export const ReportLabBlockContainer = forwardRef<HTMLDivElement, ReportLabBlockContainerProps>((props, ref) => {
  const { id, children, blockLayout, pageFooterRef, pageNumber } = props;

  const hasBlockMultiPagesFF = useHasFF('block_multi_pages');

  const blockRef = useRef<HTMLDivElement>(null);
  const generalHeaderRef = useRef<HTMLDivElement | null>(null);
  const prefaceRef = useRef<HTMLElement | null>(null);
  const trailerRef = useRef<HTMLElement | null>(null);
  const lastDataRowBoundariesRef = useRef<number[]>([]);
  const setGeneralHeaderElement = useCallback((element: HTMLDivElement | null) => {
    generalHeaderRef.current = element;
  }, []);
  const setPrefaceElement = useCallback((element: HTMLElement | null) => {
    prefaceRef.current = element;
  }, []);
  const setTrailerElement = useCallback((element: HTMLElement | null) => {
    trailerRef.current = element;
  }, []);
  const blockContextValue = useMemo(
    () => ({ blockRef, setGeneralHeaderElement, setPrefaceElement, setTrailerElement, pageNumber }),
    [pageNumber, setGeneralHeaderElement, setPrefaceElement, setTrailerElement],
  );

  const isExpanded = useRecoilValue(blockExpandedState(id));
  const isReport = useRecoilValue(isReportState);
  const { inPrintMode } = useAppPrintMode();
  const setOverflowError = useSetRecoilState(blockOverflowErrorState(id));
  const setColumnHeaderHeight = useSetRecoilState(blockColumnHeaderHeightState(id));
  const setFullContentHeight = useSetRecoilState(blockFullContentHeightState(id));
  const setTitleBarHeight = useSetRecoilState(blockTitleBarHeightState(id));
  const setDataRowHeight = useSetRecoilState(blockDataRowHeightState(id));
  const setDataRowBoundaries = useSetRecoilState(blockDataRowBoundariesState(id));
  const setBelowDataTailHeight = useSetRecoilState(blockBelowDataTailHeightState(id));
  const overflowError = useAlertOutline(id, blockRef, pageFooterRef);

  const setPrefaceHeight = useSetRecoilState(blockPrefaceHeightState(id));
  const setTrailerHeight = useSetRecoilState(blockTrailerHeightState(id));
  const customExpandsByRowSlice = useRecoilValue(blockCustomExpandsByRowSlice(id));
  const canExpand = useRecoilValue(blockCanExpandState(id));
  const continuationCount = useRecoilValue(blockContinuationPageCount(id));
  const fullContentHeight = useRecoilValue(blockFullContentHeightState(id));
  const sourcePageVisibleHeight = useRecoilValue(blockSourcePageVisibleHeight(id));
  const trailerHeightPx = useRecoilValue(blockTrailerHeightState(id));
  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const isSelected = selectedBlocks.includes(id);
  const showExpandIndicator = hasBlockMultiPagesFF && isReport && !inPrintMode && !isExpanded && canExpand;
  // Guard on measured height so the source block doesn't flash-collapse before measurement
  const isExpandedWithContinuations = isExpanded && continuationCount > 0 && fullContentHeight > 0;
  const scrollPaddingBottomPx = trailerScrollPaddingPx({
    trailerHeightPx,
    isReport: !!isReport,
    inPrintMode,
    isExpanded,
    customExpandsByRowSlice: !!customExpandsByRowSlice,
  });
  const displayedError = showExpandIndicator || isExpanded ? undefined : overflowError;

  useEffect(() => {
    setOverflowError(displayedError);
  }, [displayedError, setOverflowError]);

  const measureBlockChromeAndHeight = useCallback(() => {
    if (!blockRef.current) return;

    setTitleBarHeight(generalHeaderRef.current?.offsetHeight ?? 0);

    if (!customExpandsByRowSlice) {
      const el = blockRef.current;
      const scale = el.offsetWidth > 0 ? el.getBoundingClientRect().width / el.offsetWidth : 1;
      const agWrapper = blockRef.current.querySelector<HTMLElement>('.ag-root-wrapper');
      const dataRows = Array.from(blockRef.current.querySelectorAll<HTMLElement>('.ag-center-cols-container .ag-row'));
      const firstDataRow = dataRows[0] ?? null;

      const naturalHeight = measureNaturalContentHeight(agWrapper, generalHeaderRef.current);
      const preface = prefaceRef.current?.offsetHeight ?? 0;
      const { trailerPx, tailPx } = measureTrailerAndTail(blockRef.current, trailerRef.current, scale);
      setPrefaceHeight(preface);
      setTrailerHeight(trailerPx);
      setFullContentHeight(naturalHeight > 0 ? naturalHeight + preface + trailerPx : 0);

      const columnHeader = findElementByDataAttr(blockRef.current, GRID_COLUMN_HEADER_DATA_ATTR);
      if (columnHeader) {
        setColumnHeaderHeight(columnHeader.offsetHeight);
      }

      if (firstDataRow && firstDataRow.offsetHeight > 0) {
        setDataRowHeight(firstDataRow.offsetHeight);
      }

      const boundaries = measureDataRowBoundaries(dataRows, scale);
      const previous = lastDataRowBoundariesRef.current;
      const changed = boundaries.length !== previous.length || boundaries.some((b, i) => b !== previous[i]);
      if (boundaries.length > 0 && changed) {
        lastDataRowBoundariesRef.current = boundaries;
        setDataRowBoundaries(boundaries);
      }

      setBelowDataTailHeight(tailPx);
    }
  }, [
    customExpandsByRowSlice,
    setBelowDataTailHeight,
    setColumnHeaderHeight,
    setDataRowBoundaries,
    setDataRowHeight,
    setFullContentHeight,
    setPrefaceHeight,
    setTitleBarHeight,
    setTrailerHeight,
  ]);

  const measurementFrameRef = useRef<number | null>(null);
  const observedColumnHeaderRef = useRef<HTMLElement | null>(null);
  const observedDataRowRef = useRef<HTMLElement | null>(null);
  const observedPrefaceRef = useRef<HTMLElement | null>(null);
  const observedTrailerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = blockRef.current;
    if (!el) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      scheduleMeasurement();
    });

    const syncObservedElements = () => {
      const generalHeader = generalHeaderRef.current;
      if (generalHeader) {
        resizeObserver.observe(generalHeader);
      }

      const columnHeader = findElementByDataAttr(el, GRID_COLUMN_HEADER_DATA_ATTR);
      if (observedColumnHeaderRef.current && observedColumnHeaderRef.current !== columnHeader) {
        resizeObserver.unobserve(observedColumnHeaderRef.current);
      }
      if (columnHeader && observedColumnHeaderRef.current !== columnHeader) {
        resizeObserver.observe(columnHeader);
      }
      observedColumnHeaderRef.current = columnHeader;

      const dataRow = findFirstDataRow(el);
      if (observedDataRowRef.current && observedDataRowRef.current !== dataRow) {
        resizeObserver.unobserve(observedDataRowRef.current);
      }
      if (dataRow && observedDataRowRef.current !== dataRow) {
        resizeObserver.observe(dataRow);
      }
      observedDataRowRef.current = dataRow;

      const preface = prefaceRef.current;
      if (observedPrefaceRef.current && observedPrefaceRef.current !== preface) {
        resizeObserver.unobserve(observedPrefaceRef.current);
      }
      if (preface && observedPrefaceRef.current !== preface) {
        resizeObserver.observe(preface);
      }
      observedPrefaceRef.current = preface;

      const trailer = trailerRef.current;
      if (observedTrailerRef.current && observedTrailerRef.current !== trailer) {
        resizeObserver.unobserve(observedTrailerRef.current);
      }
      if (trailer && observedTrailerRef.current !== trailer) {
        resizeObserver.observe(trailer);
      }
      observedTrailerRef.current = trailer;
    };

    const scheduleMeasurement = () => {
      if (measurementFrameRef.current != null) return;

      measurementFrameRef.current = requestAnimationFrame(() => {
        measurementFrameRef.current = null;
        syncObservedElements();
        measureBlockChromeAndHeight();
      });
    };

    resizeObserver.observe(el);
    scheduleMeasurement();

    const mutationObserver = new MutationObserver(() => {
      scheduleMeasurement();
    });
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      if (measurementFrameRef.current != null) {
        cancelAnimationFrame(measurementFrameRef.current);
      }
      measurementFrameRef.current = null;
      observedColumnHeaderRef.current = null;
      observedDataRowRef.current = null;
      observedPrefaceRef.current = null;
      observedTrailerRef.current = null;
    };
  }, [measureBlockChromeAndHeight]);

  return (
    <OverflowErrorWrapper
      ref={ref}
      error={displayedError}
      $expandable={showExpandIndicator}
      $expanded={isExpanded}
      $hasContinuations={isExpandedWithContinuations}
      $selected={isSelected}
      $clipHeightPx={isExpandedWithContinuations && !customExpandsByRowSlice ? sourcePageVisibleHeight : undefined}
    >
      <BlockContext.Provider value={blockContextValue}>
        <BorderContainer id={id} isExpanded={isExpanded}>
          <BlockContainer
            ref={blockRef}
            height={blockLayout?.h ?? 0}
            width={blockLayout?.w ?? 0}
            $scrollPaddingBottomPx={scrollPaddingBottomPx}
            $expanded={isExpanded}
            $clipHeightPx={
              isExpandedWithContinuations && !customExpandsByRowSlice ? sourcePageVisibleHeight : undefined
            }
          >
            {children}
          </BlockContainer>
        </BorderContainer>
      </BlockContext.Provider>
      {hasBlockMultiPagesFF && !isExpanded && <ExpandBadge id={id} />}
      {hasBlockMultiPagesFF && isExpanded && <ContractBadge id={id} />}
      <OverflowErrorMessage error={displayedError} />
    </OverflowErrorWrapper>
  );
});
ReportLabBlockContainer.displayName = 'ReportLabBlockContainer';

const OverflowErrorWrapper = styled.div<{
  error: OverflowError | undefined;
  $expandable: boolean;
  $expanded: boolean;
  $hasContinuations: boolean;
  $selected: boolean;
  $clipHeightPx?: number;
}>`
  position: relative;

  width: 100%;
  height: 100%;
  ${({ $clipHeightPx }) =>
    $clipHeightPx !== undefined &&
    css`
      max-height: ${$clipHeightPx}px;
    `}

  & div:has(.${BLOCK_CONTAINER_CLASS}) {
    height: 100%;
    width: 100%;
  }

  ::before {
    content: '\\a0';
    display: block;
    z-index: ${ZIndex.Cover};
    width: 100%;
    height: 100%;
    position: absolute;
    pointer-events: none;
    box-sizing: border-box;
    ${({ error, $expandable, $expanded, $hasContinuations, $selected }) => {
      if ($expanded) {
        const color = $selected ? GetColor.ActiveBlue : GetColor.GreyScale.Grey30;
        return css`
          border: 2px solid ${color};
          ${$hasContinuations &&
          css`
            border-bottom: 2px dashed ${color};
          `}
        `;
      }
      if ($expandable) {
        return css`
          border-bottom: 3px solid ${GetColor.DarkBlue};
          transition: border-bottom-color 80ms ease-in-out;
        `;
      }
      if (!error) {
        return undefined;
      }

      switch (error.position) {
        case 'Bottom':
          return css`
            border-bottom: 3px solid ${GetColor.Error};
          `;
        case 'Right':
          return css`
            border-right: 3px solid ${GetColor.Error};
          `;
        case 'Bottom-Right':
          return css`
            border-bottom: 3px solid ${GetColor.Error};
            border-right: 3px solid ${GetColor.Error};
          `;
        case 'Surround':
          return css`
            border: 3px solid ${GetColor.Error};
          `;
        default:
          throw assertExhaustive(error.position);
      }
    }}
  }

  ${({ $expandable, $selected }) =>
    $expandable &&
    !$selected &&
    css`
      &:hover::before {
        border-bottom-color: ${GetColor.DeepDarkBlue};
      }
      &:active::before {
        border-bottom-color: ${GetColor.ActiveBlue};
      }
    `}
`;

const OverflowMessageWrapper = styled.div<{ error: OverflowError }>`
  z-index: ${ZIndex.Cover};
  width: 100%;
  height: 100%;
  position: absolute;
  pointer-events: none;
  ${({ error }) => {
    switch (error.position) {
      case 'Bottom':
      case 'Surround':
        return css`
          transform: translateX(50%);
        `;
      case 'Right':
        return css`
          transform: translate(100%, -50%);
        `;
      case 'Bottom-Right':
        return css`
          transform: translateX(100%);
        `;
      default:
        throw assertExhaustive(error.position);
    }
  }}
`;

const OverflowMessageContent = styled.div<{ error: OverflowError }>`
  color: ${GetColor.White};
  background-color: ${GetColor.Error};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 2px;
  gap: 5px;
  white-space: pre;
  font-size: 1rem;
  ${({ error }) => {
    switch (error.position) {
      case 'Bottom':
      case 'Surround':
        return css`
          transform: translate(-50%, -100%);
        `;
      case 'Right':
        return css`
          transform-origin: left;
          transform: rotate(90deg) translate(-50%, 50%);
        `;
      case 'Bottom-Right':
        return css`
          transform: translate(-100%, -100%);
        `;
      default:
        throw assertExhaustive(error.position);
    }
  }}
`;

const BlockContainer = styled.div<{
  width: number;
  height: number;
  $scrollPaddingBottomPx: number;
  $clipHeightPx?: number;
  $expanded: boolean;
}>`
  z-index: ${ZIndex.Front};
  ${({ $expanded }) =>
    $expanded
      ? // `clip` is not a scroll container, so the browser cannot focus-scroll an expanded
        // block's slice out of alignment with its continuation pages
        css`
          overflow: clip;
        `
      : css`
          overflow: hidden;
        `}
  ${({ $scrollPaddingBottomPx }) =>
    $scrollPaddingBottomPx !== 0 &&
    css`
      scroll-padding-bottom: ${$scrollPaddingBottomPx}px;
    `}
  ${({ $clipHeightPx }) =>
    $clipHeightPx !== undefined &&
    css`
      max-height: ${$clipHeightPx}px;
    `}
`;

const OverflowErrorMessage = ({ error }: { error: OverflowError | undefined }) => {
  const hasMultiPages = useHasFF('block_multi_pages');

  if (!error) {
    return null;
  }

  const renderContent = () => {
    switch (error.position) {
      case 'Bottom':
        return (
          <>
            <Icon prefix="fal" type="arrow-down" />
            {error.message}
            <Icon prefix="fal" type="arrow-down" />
          </>
        );
      case 'Right':
      case 'Surround':
        return (
          <>
            <Icon prefix="fal" type="arrow-up" />
            {error.message}
            <Icon prefix="fal" type="arrow-up" />
          </>
        );
      case 'Bottom-Right':
        return (
          <>
            <Icon prefix="fal" type="arrow-down-right" />
            {`${error.message} `}
            {!hasMultiPages && <Icon prefix="fal" type="arrow-down-right" style={{ opacity: 0 }} />}
          </>
        );
      default:
        throw assertExhaustive(error.position);
    }
  };

  return (
    <OverflowMessageWrapper error={error}>
      <OverflowMessageContent error={error}>{renderContent()}</OverflowMessageContent>
    </OverflowMessageWrapper>
  );
};
